/**
 * Wholesale Hemp Farms interactions. Small, framework free, loaded as a module.
 * Everything is server rendered; these components only add behaviour.
 */

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

/**
 * Section textures (paper grain) are CSS backgrounds on the DARK and GRAY section wrappers. They only switch on (class is-tex) when a section comes near the viewport.
 */
const TEXTURED = '.whf-section.color-whf-dark, .whf-section.color-whf-gray, .whf-pdp.color-whf-dark';
const textureObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-tex');
            textureObserver?.unobserve(entry.target);
          }
        },
        { rootMargin: '400px 0px' }
      )
    : null;
/** @param {ParentNode} root */
const observeTextures = (root) =>
  root.querySelectorAll(TEXTURED).forEach((el) => (textureObserver ? textureObserver.observe(el) : el.classList.add('is-tex')));
observeTextures(document);
document.addEventListener('shopify:section:load', (event) => observeTextures(/** @type {Element} */ (event.target)));

const lockScroll = (on) => document.documentElement.toggleAttribute('scroll-lock', on);
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * Adds one of a variant to the cart. Shopify's cart actions (configured by Horizon) update the cart
 * drawer, bubble and totals; without them the AJAX cart API is used and the visitor goes to /cart.
 * @param {string} id - Variant id.
 * @returns {Promise<boolean>} true when the page's cart UI was updated in place.
 */
async function addVariant(id) {
  // @ts-ignore Shopify standard actions
  const actions = window.Shopify?.actions;
  if (actions?.updateCart) {
    try {
      await actions.updateCart({ lines: [{ merchandiseId: id, quantity: 1 }] }, { event: { context: 'product' } });
      return true;
    } catch (error) {
      // Storefront API unavailable: fall through to the AJAX cart (it also reports sold out variants)
      console.warn('[whf] cart action failed, using the AJAX cart', error);
    }
  }
  // @ts-ignore Shopify routes
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
  });
  if (!res.ok) throw new Error(`Cart add failed (${res.status})`);
  return false;
}

/** Opens Horizon's cart drawer, or the cart page when there is no drawer. On the cart page nothing opens. */
function openCartDrawer() {
  if (document.querySelector('main[data-template="cart"]')) return;
  const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
  if (drawer?.open) {
    if (!drawer.isOpen) drawer.open();
  } else {
    // @ts-ignore Shopify routes
    window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
  }
}

/**
 * Add to cart buttons on WHF product cards ([data-whf-card-add] holds the variant id) and in the
 * cart drawer's "You may also like" row.
 * @param {HTMLButtonElement} button
 */
async function addFromButton(button) {
  const id = button.dataset.whfCardAdd;
  if (!id || button.getAttribute('aria-busy') === 'true') return;
  const text = button.querySelector('.whf-pcard__button-text, [data-whf-button-text]') || button;
  const label = text.textContent;
  button.setAttribute('aria-busy', 'true');
  let message = '';
  try {
    const inPlace = await addVariant(id);
    /** @type {any} */ (document.querySelector('whf-header'))?.setMega?.(false);
    if (!inPlace) {
      window.location.href = `${/** @type {any} */ (window).Shopify?.routes?.root || '/'}cart`;
      return;
    }
    openCartDrawer();
    message = 'Added';
    button.classList.add('is-added');
  } catch (error) {
    console.warn('[whf] add to cart', error);
    message = 'Could not add';
  } finally {
    button.removeAttribute('aria-busy');
  }
  text.textContent = message;
  window.setTimeout(() => {
    text.textContent = label;
    button.classList.remove('is-added');
  }, 1800);
}

document.addEventListener('click', (event) => {
  const button = /** @type {HTMLButtonElement|null} */ (
    /** @type {Element} */ (event.target).closest?.('[data-whf-card-add]') ?? null
  );
  if (!button || button.disabled) return;
  event.preventDefault();
  addFromButton(button);
});

// A click or tap anywhere outside the open cart drawer lands on its dark overlay (.page-wrapper::after,
// assets/whf-theme.css) and closes the cart
document.addEventListener('click', (event) => {
  const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
  if (drawer?.isOpen && event.target === document.querySelector('.page-wrapper')) drawer.close();
});

// Horizon moves focus back to the cart icon when the cart closes. After a key press while the cart was open,
// Chrome shows that focus as a keyboard ring even when the cart was closed with the mouse, so the icon is
// marked when the last input was a mouse or touch (no ring, assets/whf-theme.css). Keyboard users keep it.
let whfPointerInput = false;
document.addEventListener('pointerdown', () => (whfPointerInput = true), true);
document.addEventListener(
  'keydown',
  () => {
    whfPointerInput = false;
    document.querySelector('.whf-header__cart[data-whf-pointer-focus]')?.removeAttribute('data-whf-pointer-focus');
  },
  true
);
document.addEventListener('focusin', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement && target.matches('.whf-header__cart')) {
    target.toggleAttribute('data-whf-pointer-focus', whfPointerInput);
  }
});

/**
 * <whf-header>: Shop mega menu (hover or click on desktop) and the drawer menu below 1100px.
 * Each mega menu category owns a panel. Hover switches panels; the first click (or Enter / Space)
 * on a category only shows its panel and keeps the menu open, a second click follows the link.
 */
class WhfHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-whf-mega-toggle]');
    this.mega = this.querySelector('.whf-mega');
    this.drawer = this.querySelector('.whf-drawer');
    this.cats = /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('[data-whf-mega-cat]')]);
    this.armed = null;
    this.pinned = false;
    this.openTimer = 0;
    this.closeTimer = 0;
    this.hoverTimer = 0;

    if (this.toggle && this.mega) {
      // A click pins the menu open: it then stays open when the pointer leaves, until a click outside,
      // Escape or a second click on Shop. A click on a menu opened by hovering only pins it.
      // event.detail is 0 for keyboard activation: move focus into the panel so it can be tabbed through
      this.toggle.addEventListener('click', (event) => {
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
        if (isOpen && !this.pinned) {
          this.pinned = true;
          return;
        }
        this.setMega(!isOpen, event.detail === 0);
        this.pinned = !isOpen;
      });
      const enter = () => {
        if (!finePointer()) return;
        clearTimeout(this.closeTimer);
        this.openTimer = window.setTimeout(() => this.setMega(true), 90);
      };
      const leave = () => {
        if (!finePointer()) return;
        clearTimeout(this.openTimer);
        if (this.pinned) return;
        this.closeTimer = window.setTimeout(() => this.setMega(false), 220);
      };
      this.toggle.addEventListener('pointerenter', enter);
      this.toggle.addEventListener('pointerleave', leave);
      this.mega.addEventListener('pointerenter', () => clearTimeout(this.closeTimer));
      this.mega.addEventListener('pointerleave', leave);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.toggle.getAttribute('aria-expanded') === 'true') {
          this.setMega(false);
          this.toggle.focus();
        }
      });
      this.addEventListener('focusout', (event) => {
        const next = /** @type {Node|null} */ (event.relatedTarget);
        if (next && !this.mega.contains(next) && next !== this.toggle) this.setMega(false);
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(/** @type {Node} */ (event.target))) this.setMega(false);
      });

      this.cats.forEach((cat, index) => {
        cat.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.hoverTimer);
          this.hoverTimer = window.setTimeout(() => this.showPanel(cat), 70);
        });
        cat.addEventListener('pointerleave', () => clearTimeout(this.hoverTimer));
        cat.addEventListener('click', (event) => {
          if (this.armed === cat) return; // second click: follow the link
          event.preventDefault();
          this.showPanel(cat);
          this.armed = cat;
          this.pinned = true;
        });
        cat.addEventListener('keydown', (event) => {
          if (event.key === ' ') {
            event.preventDefault();
            this.showPanel(cat);
            this.armed = cat;
          } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const next = this.cats[(index + step + this.cats.length) % this.cats.length];
            next.focus();
            this.showPanel(next);
          }
        });
      });

      // Theme editor: selecting a category block opens the menu on its panel
      const section = this.closest('.shopify-section');
      document.addEventListener('shopify:block:select', (event) => {
        const target = /** @type {Element} */ (event.target);
        if (!section?.contains(target)) return;
        const cat = /** @type {HTMLAnchorElement|null} */ (target.querySelector('[data-whf-mega-cat]'));
        if (!cat) return;
        this.setMega(true);
        this.showPanel(cat);
      });
    }

    this.initAccount();

    this.querySelectorAll('[data-whf-drawer-open]').forEach((button) =>
      button.addEventListener('click', () => this.openDrawer())
    );
    this.querySelector('[data-whf-drawer-close]')?.addEventListener('click', () => this.closeDrawer());
    this.drawer?.addEventListener('click', (event) => {
      if (event.target === this.drawer) this.closeDrawer();
      if (/** @type {Element} */ (event.target).closest('a')) this.closeDrawer();
    });
    this.drawer?.addEventListener('close', () => {
      lockScroll(false);
      this.querySelectorAll('[data-whf-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    });
    // One category open at a time in the drawer
    this.drawer?.addEventListener(
      'toggle',
      (event) => {
        const opened = /** @type {HTMLDetailsElement} */ (event.target);
        if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
        this.drawer?.querySelectorAll('details[open]').forEach((d) => d !== opened && d.removeAttribute('open'));
      },
      true
    );
    window.matchMedia('(min-width: 1100px)').addEventListener('change', (mq) => {
      if (mq.matches) this.closeDrawer();
      else this.setMega(false);
    });
  }

  /** @param {HTMLAnchorElement} cat */
  showPanel(cat) {
    if (cat.getAttribute('aria-expanded') === 'true') return;
    if (this.armed && this.armed !== cat) this.armed = null;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (const other of this.cats) {
      const active = other === cat;
      other.classList.toggle('is-active', active);
      other.setAttribute('aria-expanded', String(active));
      const panel = document.getElementById(other.getAttribute('aria-controls') || '');
      if (!panel) continue;
      panel.hidden = !active;
      panel.classList.toggle('is-entering', active && !reduce);
    }
  }

  /**
   * Account menu (profile icon): hover opens it on fine pointers, a click or tap toggles it (and keeps it
   * open when the pointer leaves). Escape, a click outside or focus moving out of it closes it.
   */
  initAccount() {
    this.account = /** @type {HTMLElement|null} */ (this.querySelector('[data-whf-account]'));
    this.accountToggle = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-whf-account-toggle]'));
    this.accountPanel = /** @type {HTMLElement|null} */ (this.querySelector('#WhfAccountPanel'));
    if (!this.account || !this.accountToggle || !this.accountPanel) return;
    this.accountPinned = false;
    this.accountTimer = 0;

    this.accountToggle.addEventListener('click', (event) => {
      clearTimeout(this.accountTimer);
      const isOpen = this.accountToggle?.getAttribute('aria-expanded') === 'true';
      // A click on a menu opened by hovering only pins it open
      if (isOpen && !this.accountPinned && event.detail !== 0 && finePointer()) {
        this.accountPinned = true;
        return;
      }
      this.setAccount(!isOpen, event.detail === 0);
      this.accountPinned = !isOpen;
    });
    this.account.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !finePointer()) return;
      clearTimeout(this.accountTimer);
      this.accountTimer = window.setTimeout(() => this.setAccount(true), 90);
    });
    this.account.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || !finePointer()) return;
      clearTimeout(this.accountTimer);
      if (this.accountPinned) return;
      this.accountTimer = window.setTimeout(() => this.setAccount(false), 220);
    });
    this.account.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || this.accountToggle?.getAttribute('aria-expanded') !== 'true') return;
      event.stopPropagation();
      this.setAccount(false);
      this.accountToggle?.focus();
    });
    this.account.addEventListener('focusout', (event) => {
      const next = /** @type {Node|null} */ (event.relatedTarget);
      if (next && !this.account?.contains(next)) this.setAccount(false);
    });
    document.addEventListener('click', (event) => {
      if (!this.account?.contains(/** @type {Node} */ (event.target))) this.setAccount(false);
    });
  }

  /**
   * @param {boolean} open
   * @param {boolean} [focusFirst] - Move focus to the first link (keyboard opening).
   */
  setAccount(open, focusFirst = false) {
    const toggle = this.accountToggle;
    const panel = this.accountPanel;
    if (!toggle || !panel) return;
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    if (open === isOpen) return;
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      this.setMega(false);
      panel.hidden = false;
      requestAnimationFrame(() => panel.classList.add('is-open'));
      if (focusFirst) /** @type {HTMLElement|null} */ (panel.querySelector('a'))?.focus();
    } else {
      panel.classList.remove('is-open');
      panel.hidden = true;
      this.accountPinned = false;
    }
  }

  setMega(open, focusFirst = false) {
    if (!this.mega || !this.toggle) return;
    const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
    if (open === isOpen) return;
    this.toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      this.setAccount?.(false);
      // Every opening starts on the default panel (the current collection's category, or the first)
      const start = this.cats.find((c) => c.hasAttribute('data-whf-default')) || this.cats[0];
      if (start) this.showPanel(start);
      this.armed = null;
      // Panel images are lazy; fetch them all on the first opening so switching panels is instant
      if (!this.warmed) {
        this.warmed = true;
        this.mega.querySelectorAll('img[loading="lazy"]').forEach((img) => (/** @type {HTMLImageElement} */ (img).loading = 'eager'));
      }
      this.mega.hidden = false;
      requestAnimationFrame(() => this.mega.classList.add('is-open'));
      if (focusFirst) (start || this.mega.querySelector('a'))?.focus();
    } else {
      this.mega.classList.remove('is-open');
      this.mega.hidden = true;
      this.armed = null;
      this.pinned = false;
    }
  }

  openDrawer() {
    if (!this.drawer || this.drawer.open) return;
    this.drawer.showModal();
    lockScroll(true);
    this.querySelectorAll('[data-whf-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
  }

  closeDrawer() {
    if (this.drawer?.open) this.drawer.close();
  }
}
define('whf-header', WhfHeader);

/**
 * <whf-marquee>: the highlights bar. Repeats the items until one loop is wider than the bar (so the loop
 * never shows a gap), mirrors them into the aria-hidden copy, and sets the loop duration from the
 * speed setting (px per second). With reduced motion the CSS shows the items static and nothing here runs.
 */
class WhfMarquee extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.whf-highlights__list:not([data-whf-marquee-copy])');
    this.copy = this.querySelector('[data-whf-marquee-copy]');
    if (!this.list || !this.copy || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.width = 0;
    this.observer = new ResizeObserver(() => {
      if (this.clientWidth !== this.width) this.layout();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  layout() {
    const list = /** @type {HTMLElement} */ (this.list);
    this.width = this.clientWidth;
    list.querySelectorAll('[data-whf-fill]').forEach((node) => node.remove());
    const base = [...list.children];
    const baseWidth = list.scrollWidth;
    if (!baseWidth || !base.length) return;
    let width = baseWidth;
    while (width < this.width) {
      for (const item of base) {
        const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
        clone.setAttribute('data-whf-fill', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a').forEach((a) => (a.tabIndex = -1));
        list.append(clone);
      }
      width += baseWidth;
    }
    /** @type {HTMLElement} */ (this.copy).innerHTML = list.innerHTML;
    const speed = Number(this.dataset.speed) || 40;
    this.style.setProperty('--whf-marquee-duration', `${(width / speed).toFixed(1)}s`);
  }
}
define('whf-marquee', WhfMarquee);

/**
 * Signup links. Every members offer link ([data-whf-signup-open], the mega menu's Join Free button)
 * points at the email signup: with a Klaviyo popup form ID ([data-whf-klaviyo-open]) and Klaviyo loaded
 * it opens that popup; otherwise it scrolls to the signup section on this page (#newsletter) and focuses
 * its email field; on pages without one it follows its link (/#newsletter on the home page).
 */
document.addEventListener('click', (event) => {
  const link = /** @type {HTMLAnchorElement|null} */ (
    /** @type {Element} */ (event.target).closest?.('[data-whf-signup-open]') ?? null
  );
  if (!link) return;
  const w = /** @type {any} */ (window);
  const popupId = link.dataset.whfKlaviyoOpen;
  if (popupId && (w.klaviyo || w.__klKey || w._klOnsite)) {
    event.preventDefault();
    /** @type {any} */ (document.querySelector('whf-header'))?.setMega?.(false);
    w._klOnsite = w._klOnsite || [];
    w._klOnsite.push(['openForm', popupId]);
    return;
  }
  const hash = new URL(link.href, window.location.href).hash || '#newsletter';
  const section = document.querySelector(hash);
  if (!section) return; // follow the link
  event.preventDefault();
  /** @type {any} */ (document.querySelector('whf-header'))?.setMega?.(false);
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  section.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  const field = /** @type {HTMLInputElement|null} */ (section.querySelector('input[type="email"]:not([hidden])'));
  window.setTimeout(() => field?.focus({ preventScroll: true }), reduce ? 0 : 450);
});

/**
 * <whf-klaviyo-embed>: holds Klaviyo's embed container in the email signup section. The Shopify signup
 * form shows from the start; when Klaviyo renders its form into the container, the Shopify form steps
 * aside. If Klaviyo has not rendered within data-timeout ms, the empty container is dropped.
 */
class WhfKlaviyoEmbed extends HTMLElement {
  connectedCallback() {
    const target = /** @type {HTMLElement|null} */ (this.querySelector('[data-whf-klaviyo-target]'));
    const fallback = /** @type {HTMLElement|null} */ (this.querySelector('[data-whf-klaviyo-fallback]'));
    if (!target || !fallback) return;
    const useFallback = () => {
      target.hidden = true;
      fallback.hidden = false;
      this.classList.add('is-fallback');
    };
    const useKlaviyo = () => {
      fallback.hidden = true;
      this.classList.add('is-loaded');
    };
    // After a Shopify form post the thank you or error message lives in the fallback
    if (fallback.querySelector('.whf-form-message')) return useFallback();
    const rendered = () => target.childElementCount > 0;
    if (rendered()) return useKlaviyo();
    const observer = new MutationObserver(() => {
      if (!rendered()) return;
      observer.disconnect();
      clearTimeout(timer);
      useKlaviyo();
    });
    observer.observe(target, { childList: true });
    const timer = window.setTimeout(() => {
      observer.disconnect();
      if (!rendered()) useFallback();
    }, Number(this.dataset.timeout) || 10000);
  }
}
define('whf-klaviyo-embed', WhfKlaviyoEmbed);

/**
 * <whf-review-carousel>: Judge.me's featured carousel markup (server rendered by whf-reviews-carousel)
 * as a scroll snap row. Judge.me's own arrow elements page through it a screen of cards at a time;
 * they are disabled at either end, and CSS hides them when every card fits.
 */
class WhfReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.row = /** @type {HTMLElement|null} */ (this.querySelector('.jdgm-carousel__item-wrapper'));
    this.prev = /** @type {HTMLElement|null} */ (this.querySelector('.jdgm-carousel__left-arrow'));
    this.next = /** @type {HTMLElement|null} */ (this.querySelector('.jdgm-carousel__right-arrow'));
    if (!this.row) return;
    for (const [arrow, dir] of /** @type {[HTMLElement|null, number][]} */ ([[this.prev, -1], [this.next, 1]])) {
      if (!arrow) continue;
      arrow.addEventListener('click', () => this.page(dir));
      arrow.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.page(dir);
      });
    }
    this.frame = 0;
    this.row.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
    this.observer = new ResizeObserver(() => this.queueUpdate());
    this.observer.observe(this.row);
    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** @param {number} dir - -1 for back, 1 for forward */
  page(dir) {
    const row = /** @type {HTMLElement} */ (this.row);
    const item = /** @type {HTMLElement|null} */ (row.querySelector('.jdgm-carousel-item'));
    if (!item) return;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const step = item.getBoundingClientRect().width + gap;
    const perView = Math.max(1, Math.floor((row.clientWidth + gap) / step));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row.scrollBy({ left: dir * perView * step, behavior: reduce ? 'auto' : 'smooth' });
  }

  queueUpdate() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update());
  }

  update() {
    const row = /** @type {HTMLElement} */ (this.row);
    const atStart = row.scrollLeft <= 2;
    const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 2;
    this.prev?.setAttribute('aria-disabled', String(atStart));
    this.next?.setAttribute('aria-disabled', String(atEnd));
    if (this.prev) this.prev.tabIndex = atStart ? -1 : 0;
    if (this.next) this.next.tabIndex = atEnd ? -1 : 0;
  }
}
define('whf-review-carousel', WhfReviewCarousel);

/**
 * <whf-age-gate>: overlay only. The page is fully rendered underneath; the gate shows when the inline
 * check in the section marked <html class="whf-age-pending">. Confirmation is remembered in localStorage
 * with a cookie fallback.
 */
class WhfAgeGate extends HTMLElement {
  connectedCallback() {
    this.inerted = [];
    this.querySelector('[data-whf-age-yes]')?.addEventListener('click', () => this.accept());
    if (document.documentElement.classList.contains('whf-age-pending')) this.show();

    const section = this.closest('.shopify-section');
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target === section) this.classList.add('is-previewing');
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target === section) this.classList.remove('is-previewing');
    });
  }

  show() {
    document.documentElement.toggleAttribute('scroll-lock', true);
    // Make everything except the gate inert so keyboard and screen reader focus stay in the dialog
    let node = /** @type {Element} */ (this);
    while (node.parentElement && node !== document.body) {
      for (const sibling of node.parentElement.children) {
        if (sibling !== node && !sibling.hasAttribute('inert') && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
          sibling.setAttribute('inert', '');
          this.inerted.push(sibling);
        }
      }
      node = node.parentElement;
    }
    requestAnimationFrame(() => /** @type {HTMLElement|null} */ (this.querySelector('[data-whf-age-yes]'))?.focus());
  }

  accept() {
    const days = Number(this.dataset.days) || 30;
    const until = Date.now() + days * 864e5;
    try {
      localStorage.setItem('whf-age-verified', String(until));
    } catch (e) {
      /* storage blocked: the cookie below still remembers the visitor */
    }
    document.cookie = `whf_age_verified=1; max-age=${days * 86400}; path=/; SameSite=Lax`;
    document.documentElement.classList.remove('whf-age-pending');
    document.documentElement.toggleAttribute('scroll-lock', false);
    this.inerted.forEach((el) => el.removeAttribute('inert'));
    this.inerted = [];
    /** @type {HTMLElement|null} */ (document.querySelector('#MainContent'))?.focus?.({ preventScroll: true });
  }
}
define('whf-age-gate', WhfAgeGate);

/**
 * <whf-bg-video>: lazy background video. Loads only when visible, never for reduced motion or data saver,
 * and fades in over the poster once it actually plays.
 */
class WhfBgVideo extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // @ts-ignore navigator.connection is not in every browser
    const saveData = navigator.connection?.saveData;
    if (reduce || saveData) return;
    video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (!video.src && video.dataset.src) video.src = video.dataset.src;
          video.play().catch(() => {});
        } else if (video.src) {
          video.pause();
        }
      }
    });
    io.observe(this);
  }
}
define('whf-bg-video', WhfBgVideo);

/**
 * <whf-video-embed>: swaps the video still for a YouTube or Vimeo player on click (no third party
 * scripts until the visitor asks for the video).
 */
class WhfVideoEmbed extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-whf-play]')?.addEventListener('click', () => this.play());
  }

  play() {
    const { youtubeId, vimeoId, title } = this.dataset;
    let src = '';
    if (youtubeId) src = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&playsinline=1`;
    else if (vimeoId) src = `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
    if (!src) return;
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = title || 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    this.append(iframe);
    this.classList.add('is-playing');
    iframe.focus();
  }
}
define('whf-video-embed', WhfVideoEmbed);

/**
 * <whf-video-player>: self hosted video behind a poster. The <video> has no src and preload="none" until
 * the play button is pressed, so a large file costs nothing until the visitor asks for it.
 */
class WhfVideoPlayer extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-whf-video-play]')?.addEventListener('click', () => this.play());
  }

  play() {
    const video = /** @type {HTMLVideoElement|null} */ (this.querySelector('video'));
    if (!video?.dataset.src) return;
    if (!video.src) video.src = video.dataset.src;
    video.preload = 'auto';
    video.hidden = false;
    this.classList.add('is-playing');
    video.play().catch(() => {
      /* autoplay with sound refused: the native controls are showing, one more press plays */
    });
    video.focus();
  }
}
define('whf-video-player', WhfVideoPlayer);

/**
 * Product gallery on variant change. The WHF gallery (snippets/whf-product-media-gallery-content.liquid)
 * keeps the product's media order, so a page opens on the first image. When the customer picks a
 * variant that has its own image, the gallery Horizon swaps in opens on that image: this listener
 * runs first (capture) and sets initial-slide on the incoming gallery before media-gallery.js
 * inserts it.
 */
document.addEventListener(
  'shopify:product:select',
  (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element) || target.closest('product-card, .whf-pcard, quick-add-dialog')) return;
    // @ts-ignore promise is provided by the standard event
    event.promise
      ?.then(({ detail }) => {
        const gallery = detail?.html?.querySelector?.('media-gallery[data-whf-variant-slide]');
        const slideshow = gallery?.querySelector('slideshow-component');
        if (slideshow) slideshow.setAttribute('initial-slide', gallery.getAttribute('data-whf-variant-slide') || '0');
      })
      .catch(() => {});
  },
  true
);

/**
 * Variant changes: Horizon's variant picker fetches the page for the new variant and fires
 * `shopify:product:select` with a promise that resolves to that HTML. WHF fragments marked
 * [data-whf-sync="key"] are swapped from it, so prices and subscription prices follow the variant.
 * The Subscribe & Save choice (one time or a plan) survives the swap.
 */
document.addEventListener('shopify:product:select', (event) => {
  const target = /** @type {Element} */ (event.target);
  if (!(target instanceof Element) || target.closest('product-card, .whf-pcard')) return;
  const section = target.closest('.shopify-section') || document;
  // @ts-ignore promise is provided by the standard event
  event.promise
    ?.then(({ detail }) => {
      const html = detail?.html;
      if (!html) return;
      const subs = /** @type {any[]} */ ([...section.querySelectorAll('whf-subscription')]);
      const states = subs.map((el) => el.state?.());
      section.querySelectorAll('[data-whf-sync]').forEach((el) => {
        const next = html.querySelector(`[data-whf-sync="${el.getAttribute('data-whf-sync')}"]`);
        if (next) el.innerHTML = next.innerHTML;
      });
      subs.forEach((el, i) => el.refresh?.(states[i]));
    })
    .catch(() => {});
});

/**
 * Seal Subscriptions renders its own purchase options inside Horizon's product form. The WHF block
 * replaces them on screen (CSS hides Seal's widget, its branding and its details link), and Seal's
 * inputs are kept in step here, so the product form, and Seal's own add to cart handling, submit
 * the plan chosen in the WHF block: its selling_plan field, its one time or subscription radio and
 * its plan dropdown.
 * @param {HTMLFormElement|null} form
 * @param {string} planId - The selling plan id, or '' for a one time purchase.
 */
function syncSeal(form, planId) {
  if (!form) return;
  for (const widget of form.querySelectorAll('.sealsubs-target-element')) {
    for (const radio of /** @type {NodeListOf<HTMLInputElement>} */ (widget.querySelectorAll('input[type="radio"][name^="subs_type"]'))) {
      const wanted = planId ? radio.value !== 'one_time' : radio.value === 'one_time';
      if (wanted && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    for (const select of /** @type {NodeListOf<HTMLSelectElement>} */ (widget.querySelectorAll('select[name^="subs_interval"]'))) {
      if (planId && select.value !== planId && [...select.options].some((o) => o.value === planId)) {
        select.value = planId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
  // Every other selling_plan field of the form (Seal's hidden input) carries the same value
  for (const field of /** @type {HTMLInputElement[]} */ ([...form.elements])) {
    if (field.name === 'selling_plan' && !field.hasAttribute('data-whf-sub-input')) field.value = planId;
  }
}

/**
 * <whf-subscription>: switches between Subscribe & Save and one time purchase by setting the
 * selling_plan field of Horizon's product form (and Seal's inputs), and keeps the price block,
 * title and savings in step with the chosen plan.
 */
class WhfSubscription extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const t = /** @type {HTMLElement} */ (event.target);
      if (t.matches('[data-whf-sub-mode], [data-whf-sub-plan]')) this.apply();
    });
    this.form = /** @type {HTMLFormElement|null} */ (document.getElementById(this.dataset.formId || ''));
    // Seal renders (and re-renders on variant changes) after this block: keep its inputs in step
    if (this.form) {
      this.observer = new MutationObserver((records) => {
        if (records.some((r) => [...r.addedNodes].some((n) => n instanceof Element && (n.matches('.sealsubs-target-element') || n.querySelector('.sealsubs-target-element'))))) this.sync();
      });
      this.observer.observe(this.form, { childList: true, subtree: true });
    }
    this.refresh();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** @returns {{ mode: string|undefined, plan: string|undefined }} The current choice. */
  state() {
    return {
      mode: /** @type {HTMLInputElement|null} */ (this.querySelector('[data-whf-sub-mode]:checked'))?.value,
      plan: /** @type {HTMLSelectElement|null} */ (this.querySelector('[data-whf-sub-plan]'))?.value,
    };
  }

  /** @param {{ mode?: string, plan?: string }} [previous] - A choice to restore after a re-render. */
  refresh(previous) {
    if (previous?.mode) {
      const radio = /** @type {HTMLInputElement|null} */ (this.querySelector(`[data-whf-sub-mode][value="${previous.mode}"]`));
      if (radio) radio.checked = true;
    }
    const select = /** @type {HTMLSelectElement|null} */ (this.querySelector('[data-whf-sub-plan]'));
    if (select && previous?.plan && [...select.options].some((o) => o.value === previous.plan)) select.value = previous.plan;
    this.apply();
  }

  /** The value the product form submits: the plan id, or '' for a one time purchase. */
  get planId() {
    const input = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-whf-sub-input]'));
    return input && !input.disabled ? input.value : '';
  }

  sync() {
    syncSeal(this.form || null, this.planId);
  }

  apply() {
    const mode = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-whf-sub-mode]:checked'))?.value;
    const select = /** @type {HTMLSelectElement|null} */ (this.querySelector('[data-whf-sub-plan]'));
    const input = /** @type {HTMLInputElement|null} */ (this.querySelector('[data-whf-sub-input]'));
    if (!input) return;
    const subscribing = mode === 'subscription';
    input.value = subscribing && select ? select.value : '';
    // A disabled input is left out of the form data, so one time purchases post no selling_plan of ours
    input.disabled = !subscribing;
    this.sync();

    this.querySelectorAll('.whf-sub__option').forEach((opt) => {
      const radio = opt.querySelector('[data-whf-sub-mode]');
      opt.classList.toggle('is-selected', /** @type {HTMLInputElement} */ (radio)?.checked);
    });

    const option = select?.selectedOptions[0];
    if (option) {
      const price = this.querySelector('[data-whf-sub-price]');
      if (price) price.textContent = option.dataset.price || '';
      const was = this.querySelector('.whf-sub__was');
      if (was) was.textContent = option.dataset.compare || '';
      const save = this.querySelector('[data-whf-sub-save]');
      if (save) save.textContent = `Save ${option.dataset.saving}`;
      const title = this.querySelector('[data-whf-sub-title]');
      if (title && option.dataset.title) title.textContent = option.dataset.title;
    }

    // Price block in the same section
    const section = this.closest('.shopify-section');
    const now = section?.querySelector('[data-whf-price-now]');
    const was = /** @type {HTMLElement|null} */ (section?.querySelector('[data-whf-price-was]') ?? null);
    const note = /** @type {HTMLElement|null} */ (section?.querySelector('[data-whf-price-note]') ?? null);
    if (!now || !was || !note) return;
    if (subscribing && option) {
      now.textContent = option.dataset.priceShort || '';
      was.textContent = option.dataset.compareShort || '';
      was.hidden = option.dataset.compareShort === option.dataset.priceShort;
      const size = input.dataset.size || '';
      note.textContent = (this.dataset.subNote || '').replace('[size]', size).replace(/,\s*$/, '');
    } else {
      now.textContent = input.dataset.oneTimePrice || '';
      was.textContent = input.dataset.oneTimeCompare || '';
      was.hidden = !input.dataset.oneTimeCompare;
      note.textContent = note.dataset.oneTimeNote || '';
    }
  }
}
define('whf-subscription', WhfSubscription);

// Right before a product form submits, Seal's inputs get the WHF choice once more (Seal may have
// re-rendered its widget with its own default since the last change)
document.addEventListener(
  'submit',
  (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!(form instanceof HTMLFormElement) || !form.id) return;
    const sub = /** @type {any} */ (document.querySelector(`whf-subscription[data-form-id="${CSS.escape(form.id)}"]`));
    sub?.sync();
  },
  true
);

/**
 * <whf-recommendations>: when a product has no custom.pairs_well_with list, fetch Shopify's product
 * recommendations for this section and fill the row. Hides the section when nothing comes back.
 */
class WhfRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.mode !== 'fetch') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        fetch(url)
          .then((r) => r.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const grid = doc.querySelector('[data-whf-recs-grid]');
            const mine = this.querySelector('[data-whf-recs-grid]');
            if (grid && mine && grid.children.length) mine.innerHTML = grid.innerHTML;
            else this.hidden = true;
          })
          .catch(() => (this.hidden = true));
      },
      { rootMargin: '400px' }
    );
    io.observe(this);
  }
}
define('whf-recommendations', WhfRecommendations);

/**
 * <whf-cart-recs>: "You may also like" in Horizon's cart drawer. Asks Shopify's recommendations endpoint for
 * complementary products (Search & Discovery), falls back to related ones, and renders them through the
 * server rendered whf-cart-recommendations section. Its Add to cart buttons use the shared card handler
 * ([data-whf-card-add]), so Horizon's drawer, bubble and totals update themselves.
 * One product shows at a time: the list is a scroll snap row (swipe on touch), the arrows step one card.
 * The size dropdown sets the variant the Add to cart button adds and the price shown.
 */
class WhfCartRecs extends HTMLElement {
  connectedCallback() {
    this.load();
    this.onCartUpdate = () => {
      clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.load(), 400);
    };
    document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
    // Element listeners survive a disconnect, so bind them once
    if (this.bound) return;
    this.bound = true;
    this.addEventListener('click', (event) => {
      const arrow = /** @type {HTMLElement|null} */ (/** @type {Element} */ (event.target).closest('[data-whf-recs-dir]'));
      if (arrow) this.step(Number(arrow.dataset.whfRecsDir));
    });
    this.addEventListener('change', (event) => {
      const select = /** @type {HTMLSelectElement} */ (event.target);
      if (!select.matches('[data-whf-rec-variant]')) return;
      const option = select.selectedOptions[0];
      const card = select.closest('.whf-cart-rec');
      if (!option || !card) return;
      const button = /** @type {HTMLElement|null} */ (card.querySelector('[data-whf-card-add]'));
      if (button) button.dataset.whfCardAdd = option.value;
      const price = card.querySelector('[data-whf-rec-price]');
      if (price) price.textContent = option.dataset.price || '';
      const name = card.querySelector('[data-whf-rec-variant-name]');
      if (name) name.textContent = option.textContent;
    });
    // scroll does not bubble; listen in the capture phase so the arrows follow swipes
    this.addEventListener('scroll', () => this.updateArrows(), true);
    // The row has no width while the drawer is closed; update the arrows once it opens
    new ResizeObserver(() => this.updateArrows()).observe(this);
  }

  /** @param {number} dir - -1 for back, 1 for forward */
  step(dir) {
    const track = /** @type {HTMLElement|null} */ (this.querySelector('[data-whf-recs-track]'));
    const card = track?.firstElementChild;
    if (!track || !card) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: dir * (card.getBoundingClientRect().width + gap), behavior: reduce ? 'auto' : 'smooth' });
  }

  updateArrows() {
    const track = /** @type {HTMLElement|null} */ (this.querySelector('[data-whf-recs-track]'));
    if (!track) return;
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    this.querySelectorAll('[data-whf-recs-dir]').forEach((arrow) => {
      /** @type {HTMLButtonElement} */ (arrow).disabled = /** @type {HTMLElement} */ (arrow).dataset.whfRecsDir === '-1' ? atStart : atEnd;
    });
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
  }

  async load() {
    // Horizon's morph keeps this element's children (data-skip-subtree-update) but updates its
    // data-product-id when the first cart line changes, so read it fresh on every load
    const productId = this.dataset.productId;
    if (!productId || !this.dataset.url) return;
    for (const intent of ['complementary', 'related']) {
      try {
        const url = `${this.dataset.url}?product_id=${productId}&limit=8&intent=${intent}&section_id=whf-cart-recommendations`;
        const text = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const results = doc.querySelector('[data-whf-cart-recs-results]');
        if (results && results.querySelector('li')) {
          this.innerHTML = results.innerHTML;
          this.updateArrows();
          return;
        }
      } catch (e) {
        /* try the next intent */
      }
    }
    this.innerHTML = '';
  }
}
define('whf-cart-recs', WhfCartRecs);

/**
 * Cart page product row ([data-whf-row-recs], set by whf-product-row on /cart): the same recommendations as
 * <whf-cart-recs> (the cart's first product, complementary then related; the section skips products in the cart
 * and sold out ones). The server rendered collection comes back when the cart is empty or nothing is found.
 */
document.querySelectorAll('[data-whf-row-recs]').forEach((row) => {
  const el = /** @type {HTMLElement} */ (row);
  const grid = el.querySelector('.whf-product-row__grid');
  if (!grid) return;
  const fallback = grid.innerHTML;
  let swapped = false;
  /** @param {string | number | undefined} productId */
  const load = async (productId) => {
    if (productId) {
      for (const intent of ['complementary', 'related']) {
        try {
          const text = await (await fetch(`${el.dataset.whfRowRecs}&product_id=${productId}&intent=${intent}`)).text();
          const results = new DOMParser().parseFromString(text, 'text/html').querySelector('.whf-product-row__grid');
          if (results && results.children.length) {
            grid.innerHTML = results.innerHTML;
            swapped = true;
            return;
          }
        } catch (e) {
          /* try the next intent */
        }
      }
    }
    if (swapped) grid.innerHTML = fallback;
    swapped = false;
  };
  load(el.dataset.productId);
  /** @type {number | undefined} */
  let timer;
  document.addEventListener('shopify:cart:lines-update', () => {
    clearTimeout(timer);
    timer = window.setTimeout(async () => {
      try {
        // @ts-ignore Shopify routes
        const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
        load(cart.items[0]?.product_id);
      } catch (e) {
        /* keep the current row */
      }
    }, 400);
  });
});

/**
 * <whf-toc>: builds the "On this page" list from the article's H2 headings and highlights the
 * section being read. Headings get stable ids so the links can be shared.
 */
class WhfToc extends HTMLElement {
  connectedCallback() {
    const body = document.querySelector(this.dataset.target || '');
    const list = this.querySelector('ol');
    if (!body || !list) return;
    const headings = /** @type {HTMLElement[]} */ ([...body.querySelectorAll('h2')]);
    const used = new Set();
    const links = headings.map((h) => {
      if (!h.id) {
        let id = (h.textContent || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section';
        while (used.has(id) || document.getElementById(id)) id += '-2';
        h.id = id;
      }
      used.add(h.id);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = h.textContent || '';
      li.append(a);
      list.append(li);
      return a;
    });
    if (window.matchMedia('(max-width: 1099px)').matches) this.querySelector('details')?.removeAttribute('open');
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = headings.indexOf(/** @type {HTMLElement} */ (entry.target));
          links.forEach((l, j) => l.setAttribute('aria-current', String(i === j)));
        }
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    headings.forEach((h) => io.observe(h));
  }
}
define('whf-toc', WhfToc);
